import { z } from 'zod';

// Per-widget config for investment-balance widgets. Only `groupBy` lives here — filters
// and scope live on the parent dashboard and apply uniformly to every widget on it.

// Valid `groupBy` tokens. Kept here (rather than imported from the frontend's variance
// type) because this DTO is shared by both backend and frontend and shouldn't reach
// across into app-specific modules.
const GROUP_BY_TOKENS = ['team', 'itemType', 'integration'] as const;
const MAX_GROUP_BY_TOKENS = 2;

// Accepts an empty string (no breakdown) or up to MAX_GROUP_BY_TOKENS distinct tokens
// joined by `_`, e.g. "team", "team_itemType". Rejects unknown tokens, duplicates, and
// more than the allowed number of tokens.
const isValidGroupByCompact = (value: string): boolean => {
  if (value === '') return true;
  const parts = value.split('_');
  if (parts.length > MAX_GROUP_BY_TOKENS) return false;
  if (new Set(parts).size !== parts.length) return false;
  const allowed: readonly string[] = GROUP_BY_TOKENS;
  return parts.every((p) => allowed.includes(p));
};

const groupBySchema = z.string().refine(isValidGroupByCompact, {
  message:
    "Invalid groupBy. Expected an empty string, or one or two of 'team' / 'itemType' / 'integration' joined by '_' (e.g. 'team_itemType').",
});

export const investmentBalanceConfigDTOSchema = z.object({
  widgetId: z.number().int(),
  groupBy: groupBySchema,
});

export type InvestmentBalanceConfigDTO = z.infer<typeof investmentBalanceConfigDTOSchema>;

export function parseInvestmentBalanceConfigDTO(data: unknown): InvestmentBalanceConfigDTO {
  return investmentBalanceConfigDTOSchema.parse(data);
}

export const investmentBalanceConfigUpsertDTOSchema = investmentBalanceConfigDTOSchema
  .omit({ widgetId: true })
  .strict();

export type InvestmentBalanceConfigUpsertDTO = z.infer<
  typeof investmentBalanceConfigUpsertDTOSchema
>;
